package fr.frux.feature.site.create

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.selection.selectableGroup
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.FruxActionChip
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.components.FruxSearchField
import fr.frux.core.designsystem.icons.FruxIcons
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.site.labelRes
import fr.frux.feature.site.resources.Res
import fr.frux.feature.site.resources.site_planting_count_group
import fr.frux.feature.site.resources.site_planting_remove
import fr.frux.feature.site.resources.site_species_search
import fr.frux.feature.site.resources.site_species_search_clear
import fr.frux.shared.domain.model.PlantingCount
import fr.frux.shared.domain.model.Species
import org.jetbrains.compose.resources.stringResource

private val CHIP_SHAPE = RoundedCornerShape(50)

/**
 * Une espèce déclarée sur un lieu : son nom, « Retirer », puis ses quatre tranches. Partagé par la
 * création de lieu et l'ajout d'une espèce depuis la fiche.
 */
@Composable
fun PlantingEditor(
    speciesName: String,
    count: PlantingCount?,
    onCount: (PlantingCount) -> Unit,
    onRemove: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(FruxSpacing.xs)) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                text = speciesName,
                style = MaterialTheme.typography.titleMedium,
                modifier = Modifier.weight(1f),
            )
            FruxButton(
                text = stringResource(Res.string.site_planting_remove),
                onClick = onRemove,
                style = FruxButtonStyle.Text,
            )
        }
        PlantingCountChips(speciesName = speciesName, selected = count, onSelect = onCount)
    }
}

/** Les quatre tranches d'effectif, choix exclusif : « 1 », « 2 à 5 », « 6 à 20 », « 20+ ». */
@Composable
fun PlantingCountChips(
    speciesName: String,
    selected: PlantingCount?,
    onSelect: (PlantingCount) -> Unit,
    modifier: Modifier = Modifier,
) {
    val groupLabel = stringResource(Res.string.site_planting_count_group, speciesName)
    Row(
        modifier = modifier.selectableGroup().semantics { contentDescription = groupLabel },
        horizontalArrangement = Arrangement.spacedBy(FruxSpacing.xs),
    ) {
        PlantingCount.entries.forEach { count ->
            CountChip(
                label = stringResource(count.labelRes()),
                selected = count == selected,
                onClick = { onSelect(count) },
            )
        }
    }
}

@Composable
private fun CountChip(
    label: String,
    selected: Boolean,
    onClick: () -> Unit,
) {
    val scheme = MaterialTheme.colorScheme
    Surface(
        modifier =
            Modifier
                .heightIn(min = 40.dp)
                .selectable(selected = selected, role = Role.RadioButton, onClick = onClick),
        shape = CHIP_SHAPE,
        color = if (selected) scheme.primary else scheme.surface,
        contentColor = if (selected) scheme.onPrimary else scheme.onSurface,
        border = if (selected) null else BorderStroke(1.dp, scheme.outline),
    ) {
        Box(modifier = Modifier.padding(horizontal = 14.dp, vertical = 10.dp), contentAlignment = Alignment.Center) {
            Text(label, style = MaterialTheme.typography.labelLarge)
        }
    }
}

/** Recherche d'une espèce à ajouter, résultats en puces : toucher une puce l'ajoute. */
@Composable
fun SpeciesPicker(
    query: String,
    results: List<Species>,
    onQuery: (String) -> Unit,
    onPick: (Species) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
        FruxSearchField(
            value = query,
            onValueChange = onQuery,
            placeholder = stringResource(Res.string.site_species_search),
            clearLabel = stringResource(Res.string.site_species_search_clear),
        )
        if (results.isNotEmpty()) {
            FlowRow(
                horizontalArrangement = Arrangement.spacedBy(FruxSpacing.xs),
                verticalArrangement = Arrangement.spacedBy(FruxSpacing.xs),
            ) {
                results.forEach { species ->
                    FruxActionChip(
                        label = species.commonNameFr,
                        onClick = { onPick(species) },
                        trailingIcon = FruxIcons.Add,
                    )
                }
            }
        }
    }
}
